import { useState } from "react";
import { toast } from "sonner";
import { usePreviousProgrammes, useCreateWorkspace } from "@/lib/coachQueries";
import { Input } from "@/components/ui/input";

/**
 * Master v5 Prompt 17 — Previous Programmes (read-only history among
 * the calling coach's OWN Workspaces only — see client_record_routes.py)
 * + Start New Programme, which reuses the existing engagement-creation
 * route to open a brand-new Workspace under the SAME person, never
 * overwriting any prior Workspace's own history.
 */
export default function PreviousProgrammesTab({ workspaceId, memberId, overview }) {
  const { data, isPending, isError } = usePreviousProgrammes(workspaceId, memberId);
  const createWorkspace = useCreateWorkspace();
  const [newTitle, setNewTitle] = useState("");

  async function handleStartNew() {
    if (!overview.member_email || !newTitle.trim()) return;
    try {
      const result = await createWorkspace.mutateAsync({
        client_emails: [overview.member_email], programme_type: overview.programme_type || "executive_presence",
        access_type: overview.access_type || "individual", title: newTitle.trim(),
      });
      toast.success(`New programme "${newTitle.trim()}" started for ${overview.member_name}`);
      setNewTitle("");
      return result;
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Could not start new programme" : err.message);
    }
  }

  return (
    <div data-testid="client-360-previous-programmes-tab" className="space-y-6">
      <div className="border border-ink/10 rounded-lg p-4 bg-white/50">
        <h2 className="font-serif text-lg text-ink mb-2">Start New Programme</h2>
        <p className="text-xs text-ink-soft mb-3">
          Creates a brand-new Engagement/Workspace for {overview.member_name} — every record in this
          current Workspace stays exactly as it is.
        </p>
        <div className="flex items-center gap-2">
          <Input
            value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New programme title"
            className="max-w-xs" data-testid="client-360-new-programme-title-input"
          />
          <button
            onClick={handleStartNew}
            disabled={!newTitle.trim() || createWorkspace.isPending}
            className="text-sm font-medium bg-terracotta text-paper px-4 py-2 rounded-full hover:bg-terracotta/90 transition-colors disabled:opacity-50"
            data-testid="client-360-start-new-programme-button"
          >
            {createWorkspace.isPending ? "Starting…" : "Start New Programme"}
          </button>
        </div>
      </div>

      <div>
        <h2 className="font-serif text-lg text-ink mb-2">History</h2>
        {isPending ? (
          <p className="text-sm text-ink-soft" data-testid="client-360-previous-programmes-loading">Loading…</p>
        ) : isError ? (
          <p className="text-sm text-red-600" data-testid="client-360-previous-programmes-error">Couldn't load previous programmes.</p>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-ink-soft" data-testid="client-360-previous-programmes-empty">No previous programmes for this client.</p>
        ) : (
          <div className="space-y-2" data-testid="client-360-previous-programmes-list">
            {data.items.map((w) => (
              <div key={w.wrk_id} className="border border-ink/10 rounded-lg p-3 bg-white/50" data-testid={`client-360-previous-programme-${w.wrk_id}`}>
                <p className="text-sm text-ink font-medium">{w.title}</p>
                <p className="text-xs text-ink-soft uppercase">{w.programme_type} · {w.status} · read-only</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
